import { BaseScreenProps } from '@/features/quiz/types'
import { HEIGHT_KEY, WEIGHT_KEY } from '@/features/quiz/ed/constants'
import { InferType, object, string } from 'yup'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import React from 'react'
import ScreenTitle from '@/features/quiz/screen-title'
import QuizTextInput from '@/features/quiz/inputs/quiz-text-input'
import BottomContinueButton from '@/features/quiz/bottom-continue-button'

type Props = BaseScreenProps

const schema = object({
  weight: string()
    .required('Please enter your weight.')
    .test('weight-is-number', (value, context) => {
      const result = parseInt(value)
      if (isNaN(result))
        return context.createError({
          message: 'Please enter your weight in kg as a number.',
        })

      return true
    }),
  height: string()
    .required('Please enter your height')
    .test('height-is-number', (value, context) => {
      const result = parseInt(value)
      if (isNaN(result))
        return context.createError({
          message: 'Please enter your height in cm as a number.',
        })

      return true
    }),
})

interface FormValue extends InferType<typeof schema> {}

export default function BodyCompositionScreen({
  screenId,
  answers,
  onBatchChange,
}: Props) {
  const weightAnswer = answers[WEIGHT_KEY]
  const heightAnswer = answers[HEIGHT_KEY]

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: {
      height: heightAnswer?.type === 'number' ? `${heightAnswer.answer}` : '',
      weight: weightAnswer?.type === 'number' ? `${weightAnswer.answer}` : '',
    },
    resolver: yupResolver(schema),
  })

  const { handleSubmit, formState } = methods
  const { isValid } = formState

  function onSubmit(values: FormValue) {
    onBatchChange(
      [
        [
          HEIGHT_KEY,
          { id: HEIGHT_KEY, type: 'number', answer: parseInt(values.height) },
        ],
        [
          WEIGHT_KEY,
          { id: WEIGHT_KEY, type: 'number', answer: parseInt(values.weight) },
        ],

        // Need to put a dummy answer for body composition so that resuming quiz
        // will skip the screen properly
        [screenId, { id: screenId, type: 'boolean', answer: true }],
      ],
      true,
    )
  }

  return (
    <>
      <div className="mb-16 w-full">
        <div className="mx-auto max-w-3xl">
          <FormProvider {...methods}>
            <form
              className="flex flex-col gap-y-9 sm:gap-y-10 lg:gap-y-11"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="space-y-4 sm:space-y-5 lg:space-y-6">
                <ScreenTitle title="What is your height?" />
                <QuizTextInput name="height" label="cm" inputMode="numeric" />
              </div>
              <div className="space-y-4 sm:space-y-5 lg:space-y-6">
                <ScreenTitle title="What is your current weight?" />
                <QuizTextInput name="weight" label="kg" inputMode="numeric" />
              </div>

              <div>
                <BottomContinueButton
                  initial={false}
                  show={isValid}
                  disabled={!isValid}
                  buttonProps={{
                    type: 'submit',
                  }}
                />
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </>
  )
}
